import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "../../../utils/classNames";
import defaultAvatar from "../../../images/user.svg";
import "./index.css";

const SIZE_TOKENS = {
    sm: "32px",
    md: "48px",
    lg: "56px",
    xl: "72px",
};

const RADIUS_TOKENS = {
    sm: "10px",
    md: "14px",
    lg: "18%",
    xl: "22%",
    round: "50%",
};

const CACHE_BUST_PARAM = "v";

const resolveLogicalAvatarSource = (source) => {
    if (!source) {
        return null;
    }

    const hashIndex = source.indexOf("#");
    const hash = hashIndex >= 0 ? source.slice(hashIndex) : "";
    const sourceWithoutHash = hashIndex >= 0 ? source.slice(0, hashIndex) : source;
    const queryIndex = sourceWithoutHash.indexOf("?");
    const path = queryIndex >= 0
        ? sourceWithoutHash.slice(0, queryIndex)
        : sourceWithoutHash;
    const query = queryIndex >= 0 ? sourceWithoutHash.slice(queryIndex + 1) : "";
    const params = Array.from(new URLSearchParams(query).entries());
    const hasCacheBust = params.some(([key]) => key === CACHE_BUST_PARAM);
    const logicalParams = params
        .filter(([key]) => key !== CACHE_BUST_PARAM)
        .sort(([leftKey, leftValue], [rightKey, rightValue]) => (
            leftKey.localeCompare(rightKey) || leftValue.localeCompare(rightValue)
        ));

    return {
        hasCacheBust,
        key: JSON.stringify([path, logicalParams, hash]),
    };
};

const isSameAvatarCacheBust = (currentSource, nextSource) => {
    if (!currentSource || !nextSource || currentSource === nextSource) {
        return false;
    }

    const current = resolveLogicalAvatarSource(currentSource);
    const next = resolveLogicalAvatarSource(nextSource);
    return Boolean(
        current
        && next
        && (current.hasCacheBust || next.hasCacheBust)
        && current.key === next.key
    );
};

const resolveToken = (value, tokens) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return `${value}px`;
    }
    if (typeof value === "string" && tokens[value]) {
        return tokens[value];
    }
    return value;
};

export function Avatar({
    src,
    alt,
    size,
    radius = "lg",
    className,
    imgClassName,
    fallbackSrc = defaultAvatar,
    fallback,
    imgProps,
    style,
    id,
    defaultAvatarBackground = false,
}) {
    const resolvedSize = resolveToken(size, SIZE_TOKENS);
    const resolvedRadius = resolveToken(radius, RADIUS_TOKENS);
    const wrapperStyle = { ...(style || {}) };

    if (resolvedSize) {
        wrapperStyle["--avatar-size"] = resolvedSize;
    }

    if (resolvedRadius) {
        wrapperStyle["--avatar-radius"] = resolvedRadius;
    }

    const shouldRenderFallback = !src && Boolean(fallback);
    const resolvedSrc = src || (shouldRenderFallback ? null : fallbackSrc);
    const [imageState, setImageState] = useState(() => ({
        displaySrc: resolvedSrc,
        requestedSrc: resolvedSrc,
        usingFallback: false,
    }));
    const imageStateRef = useRef(imageState);

    useEffect(() => {
        imageStateRef.current = imageState;
    }, [imageState]);

    const requestIsSettled = imageState.requestedSrc === resolvedSrc;
    const cacheBustIsPending = isSameAvatarCacheBust(imageState.requestedSrc, resolvedSrc);
    const displaySrc = requestIsSettled
        ? (imageState.usingFallback ? (fallbackSrc || resolvedSrc) : imageState.displaySrc)
        : cacheBustIsPending
            ? imageState.displaySrc
            : resolvedSrc;

    useEffect(() => {
        const updateImageState = (nextState) => {
            imageStateRef.current = nextState;
            setImageState(nextState);
        };
        const currentState = imageStateRef.current;

        if (currentState.requestedSrc === resolvedSrc) {
            if (currentState.usingFallback && currentState.displaySrc !== fallbackSrc) {
                updateImageState({
                    displaySrc: fallbackSrc || resolvedSrc,
                    requestedSrc: resolvedSrc,
                    usingFallback: Boolean(fallbackSrc),
                });
            }
            return undefined;
        }

        if (!resolvedSrc) {
            updateImageState({
                displaySrc: null,
                requestedSrc: null,
                usingFallback: false,
            });
            return undefined;
        }

        // Only keep the previous image for a cache-bust of the same resource.
        // Identity and fallback changes render their new source immediately.
        if (!currentState.displaySrc
            || !isSameAvatarCacheBust(currentState.requestedSrc, resolvedSrc)) {
            updateImageState({
                displaySrc: resolvedSrc,
                requestedSrc: resolvedSrc,
                usingFallback: false,
            });
            return undefined;
        }

        let cancelled = false;
        const image = new Image();
        image.onload = () => {
            if (!cancelled) {
                updateImageState({
                    displaySrc: resolvedSrc,
                    requestedSrc: resolvedSrc,
                    usingFallback: false,
                });
            }
        };
        image.onerror = () => {
            if (!cancelled) {
                updateImageState({
                    displaySrc: fallbackSrc || currentState.displaySrc,
                    requestedSrc: resolvedSrc,
                    usingFallback: Boolean(fallbackSrc),
                });
            }
        };
        image.src = resolvedSrc;

        return () => {
            cancelled = true;
        };
    }, [fallbackSrc, resolvedSrc]);

    const {
        id: imgId,
        className: imgPropsClassName,
        src: _ignoredSrc,
        alt: _ignoredAlt,
        onError,
        ...restImgProps
    } = imgProps || {};
    const handleImageError = (event) => {
        onError?.(event);
        if (!fallbackSrc) {
            return;
        }
        const target = event.currentTarget;
        if (target.dataset.avatarFallbackApplied === "true") {
            return;
        }
        target.dataset.avatarFallbackApplied = "true";
        target.src = fallbackSrc;
    };

    return (
        <span
            className={classNames("mz-avatar", className, {
                "mz-avatar--default-background": defaultAvatarBackground,
            })}
            style={wrapperStyle}
        >
            {displaySrc ? (
                <img
                    key={displaySrc}
                    id={imgId || id || undefined}
                    src={displaySrc}
                    alt={alt}
                    className={classNames("mz-avatar__img", imgClassName, imgPropsClassName)}
                    onError={handleImageError}
                    {...restImgProps}
                />
            ) : shouldRenderFallback ? (
                <span className="mz-avatar__fallback">{fallback}</span>
            ) : null}
        </span>
    );
}

Avatar.propTypes = {
    src: PropTypes.string,
    alt: PropTypes.string.isRequired,
    size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string,
    imgClassName: PropTypes.string,
    fallbackSrc: PropTypes.string,
    fallback: PropTypes.node,
    imgProps: PropTypes.object,
    style: PropTypes.object,
    id: PropTypes.string,
    defaultAvatarBackground: PropTypes.bool,
};

export default Avatar;
